import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { HdBodygraphPlanetaryActivation, HdBodygraphRenderState, HdBodygraphSide } from '../../hd-bodygraph.models';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../../hd-content-language.service';
import { HdPlanetGridColorConfig } from '../hd-planet-grid-color-config';
import { getHdPlanetDisplayConfig, HdPlanetDisplayConfig, HdPlanetKey } from '../hd-planet-display-config';
import { findActivationFromList, isGateInDefinedCenter } from '../hd-planet-grid.utils';
import { HdPlanetActivationBlockClickEvent, HdPlanetActivationBlockComponent } from '../hd-planet-activation-block/hd-planet-activation-block.component';
import {
  HD_RESONANCE_BASE_BY_PLANET,
  HD_RESONANCE_LATERAL_ROWS,
  HD_RESONANCE_LEGEND,
  HD_RESONANCE_LEGEND_ICON_SRC,
  HD_RESONANCE_VERTICALS,
  HdResonanceLateralRow,
  HdResonanceLegendKind,
  HdResonanceVerticalConfig,
  resonanceBaseIconSrc,
  resonanceBaseKeynoteKey,
  resonanceBaseNameKey,
  resonanceKeynoteKey,
  resonanceLateralLabelKey,
  resonanceLateralTooltipKey,
  resonanceLegendTitleKey,
  resonancePranaKey,
  resonanceTooltipKey,
  resonanceVerticalDescriptionKey,
  resonanceVerticalKeynoteKey,
  resonanceVerticalLabelKey,
} from './hd-resonance-content';

/** View-model van één LPS-blok in de Resonance-grid. */
interface HdResonanceBlockViewModel {
  readonly activation: HdBodygraphPlanetaryActivation;
  readonly displayConfig: HdPlanetDisplayConfig;
  readonly isDefined: boolean;
  readonly keynote: string;
  readonly keynoteTooltip: string;
  readonly baseNumber: number | null;
  readonly baseIconSrc: string | null;
  readonly baseTooltip: string | null;
}

/** View-model van één verticale groepering (kolom van 3 blokken). */
interface HdResonanceVerticalViewModel {
  readonly key: HdResonanceVerticalConfig['key'];
  readonly label: string;
  readonly keynote: string;
  readonly description: string;
  readonly blocks: readonly HdResonanceBlockViewModel[];
}

/** View-model van één laterale groepering (rij), met label rechts. */
interface HdResonanceLateralViewModel {
  readonly row: HdResonanceLateralRow;
  readonly label: string;
  readonly tooltip: string;
}

interface HdResonanceLegendItem {
  readonly kind: HdResonanceLegendKind;
  readonly text: string;
  /** Mini-grid-SVG (presence/prana/…) of `null` voor een kleur-swatch (vertical/lateral). */
  readonly iconSrc: string | null;
}

/**
 * Geavanceerde "Resonance"-variant van de Lunar Planetary Square (advanced
 * LPS). Toont de 3×3 LPS-blokken met per-blok zichtbare keynote + tooltip,
 * verticale groeperingen (kolom-label boven + onder-keynote), laterale
 * groeperingen (rij-label rechts) en een legende. De LPS-blokken zelf blijven
 * dezelfde grootte en kleur als in de klassieke grid.
 *
 * Zie `.\docs\human-design\hd-advanced-lunar-planetary-square.md`.
 */
@Component({
  selector: 'hd-resonance-lunar-planetary-square',
  templateUrl: './hd-resonance-lunar-planetary-square.component.html',
  styleUrl: './hd-resonance-lunar-planetary-square.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HdPlanetActivationBlockComponent],
  host: {
    '[class.side-design]': "side() === 'design'",
    '[class.side-personality]': "side() === 'personality'",
  },
})
export class HdResonanceLunarPlanetarySquareComponent {
  readonly activations = input.required<readonly HdBodygraphPlanetaryActivation[]>();
  readonly side = input.required<HdBodygraphSide>();
  readonly renderState = input.required<HdBodygraphRenderState>();
  readonly colorConfig = input.required<HdPlanetGridColorConfig>();
  /** Lijn-nummers van de tegenovergestelde nodes, voor gele lijn-highlight. */
  readonly matchLines = input<ReadonlySet<number>>(new Set());
  /** Lijn-nummers van het profiel (Sun/Earth) beide zijden, voor groene lijn-highlight. */
  readonly profileLines = input<ReadonlySet<number>>(new Set());
  readonly blockClicked = output<HdPlanetActivationBlockClickEvent>();

  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  protected readonly lateralRows = HD_RESONANCE_LATERAL_ROWS;

  /** Base-icoon staat rechtsboven op beide zijden (Figma-conform). */
  protected readonly baseSymbolPlacement = 'top-right' as const;

  protected readonly title = computed(() =>
    this.translate(resonanceLegendTitleKey(this.side())),
  );

  /**
   * Prana-indicator (rechtsboven, beide zijden): laterals = prana uit (OUT,
   * horizontale pijl → expressie naar buiten), verticals = prana in (IN,
   * verticale pijl → basis van de levensenergie).
   */
  protected readonly pranaWord = computed(() => this.translate(resonancePranaKey('word')));
  protected readonly pranaOutLabel = computed(() => this.translate(resonancePranaKey('out')));
  protected readonly pranaInLabel = computed(() => this.translate(resonancePranaKey('in')));
  protected readonly lateralReactivityLabel = computed(() =>
    this.translate(resonancePranaKey('lateralReactivity')),
  );
  /**
   * "Vertical Shaping" per woord opgesplitst zodat elk woord als aparte
   * geroteerde kolom naast elkaar getoond kan worden (Figma-conform).
   */
  protected readonly verticalShapingLines = computed(() =>
    this.translate(resonancePranaKey('verticalShaping')).split(' '),
  );

  /**
   * De verticale groeperingen in weergavevolgorde. Personality leest
   * links→rechts (internalBalance, externalized, oxygen); design is
   * gespiegeld en toont ze in omgekeerde volgorde.
   */
  protected readonly verticals = computed<readonly HdResonanceVerticalViewModel[]>(() => {
    const side = this.side();
    const ordered = side === 'design'
      ? [...HD_RESONANCE_VERTICALS].reverse()
      : HD_RESONANCE_VERTICALS;
    return ordered
      .map(config => this.createVerticalViewModel(config, side))
      .filter((vertical): vertical is HdResonanceVerticalViewModel => vertical !== null);
  });

  protected readonly laterals = computed<readonly HdResonanceLateralViewModel[]>(() => {
    const side = this.side();
    return this.lateralRows.map(row => ({
      row,
      label: this.translate(resonanceLateralLabelKey(row, side)),
      // Design-kant heeft (nog) geen lateral-tooltipteksten; personality wel.
      tooltip: side === 'design' ? '' : this.translate(resonanceLateralTooltipKey(row, side)),
    }));
  });

  protected readonly legendItems = computed<readonly HdResonanceLegendItem[]>(() => {
    const side = this.side();
    const sideItems: readonly HdResonanceLegendItem[] = side === 'design'
      ? [this.imageLegendItem('presence'), this.imageLegendItem('prana')]
      : [this.imageLegendItem('mundaneConsciousness'), this.imageLegendItem('martianDiagonals')];
    const commonItems: readonly HdResonanceLegendItem[] = [
      this.swatchLegendItem('vertical'),
      this.swatchLegendItem('lateral'),
    ];
    return [...sideItems, ...commonItems];
  });

  private imageLegendItem(
    kind: keyof typeof HD_RESONANCE_LEGEND_ICON_SRC,
  ): HdResonanceLegendItem {
    return {
      kind,
      text: this.translate(HD_RESONANCE_LEGEND[kind]),
      iconSrc: HD_RESONANCE_LEGEND_ICON_SRC[kind],
    };
  }

  private swatchLegendItem(kind: 'vertical' | 'lateral'): HdResonanceLegendItem {
    return {
      kind,
      text: this.translate(HD_RESONANCE_LEGEND[kind]),
      iconSrc: null,
    };
  }

  private createVerticalViewModel(
    config: HdResonanceVerticalConfig,
    side: HdBodygraphSide,
  ): HdResonanceVerticalViewModel | null {
    const blocks = config.planetKeys
      .map(planetKey => this.createBlockViewModel(planetKey, side))
      .filter((block): block is HdResonanceBlockViewModel => block !== null);

    if (blocks.length === 0) {
      return null;
    }

    return {
      key: config.key,
      label: this.translate(resonanceVerticalLabelKey(config.key, side)),
      keynote: this.translate(resonanceVerticalKeynoteKey(config.key, side)),
      description: this.translate(resonanceVerticalDescriptionKey(config.key, side)),
      blocks,
    };
  }

  private createBlockViewModel(
    planetKey: HdPlanetKey,
    side: HdBodygraphSide,
  ): HdResonanceBlockViewModel | null {
    const displayConfig = getHdPlanetDisplayConfig(planetKey);
    const activation = findActivationFromList(this.activations(), side, displayConfig.planetId);
    if (!activation) {
      return null;
    }

    const baseNumber = this.resolveBaseNumber(planetKey, side);
    return {
      activation,
      displayConfig,
      isDefined: isGateInDefinedCenter(activation.gate, this.renderState()),
      keynote: this.translate(resonanceKeynoteKey(planetKey, side)),
      keynoteTooltip: this.translate(resonanceTooltipKey(planetKey, side)),
      baseNumber,
      baseIconSrc: baseNumber !== null ? resonanceBaseIconSrc(baseNumber) : null,
      baseTooltip: this.resolveBaseTooltip(planetKey, side),
    };
  }

  private resolveBaseNumber(planetKey: HdPlanetKey, side: HdBodygraphSide): number | null {
    if (side === 'highlight') {
      return null;
    }
    return HD_RESONANCE_BASE_BY_PLANET[side][planetKey] ?? null;
  }

  private resolveBaseTooltip(planetKey: HdPlanetKey, side: HdBodygraphSide): string | null {
    const base = this.resolveBaseNumber(planetKey, side);
    if (base === null) {
      return null;
    }
    const name = this.translate(resonanceBaseNameKey(base));
    const keynote = this.translate(resonanceBaseKeynoteKey(base));
    return `${name} — ${keynote}`;
  }

  private translate(key: string): string {
    // Lees de load-tick zodat de omliggende `computed()`s hervertalen zodra het
    // JSON-bestand van de (initiële) taal geladen is. Zonder deze dependency
    // blijft de gememoizede waarde op de fallback-taal hangen bij het eerste
    // render-frame. Zie `HdContentLanguageService.translationsLoadedTick`.
    this.contentLang.translationsLoadedTick();
    return this.transloco.translate(key, undefined, this.lang());
  }
}
